import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { mediaUrl } from "@/lib/api";
import {
  LayoutGrid,
  Film,
  Music2,
  MessageSquare,
  User,
  LogOut,
  Compass,
  Image as ImageIcon,
  CalendarDays,
  BookOpen,
} from "lucide-react";
import AudioPlayer from "@/components/AudioPlayer";

const navItems = [
  { to: "/app", label: "Overview", icon: LayoutGrid, end: true, testid: "nav-overview" },
  { to: "/app/explore", label: "Esplore Ayiti", icon: Compass, testid: "nav-explore" },
  { to: "/app/konesans", label: "Konesans", icon: BookOpen, testid: "nav-konesans" },
  { to: "/app/videos", label: "Videos", icon: Film, testid: "nav-videos" },
  { to: "/app/music", label: "Music", icon: Music2, testid: "nav-music" },
  { to: "/app/photos", label: "Photos", icon: ImageIcon, testid: "nav-photos" },
  { to: "/app/events", label: "Events & Shop", icon: CalendarDays, testid: "nav-events" },
  { to: "/app/chat", label: "Messages", icon: MessageSquare, testid: "nav-chat" },
  { to: "/app/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] grain">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden md:flex w-64 flex-col border-r border-[#2a2a2a] bg-[#0a0a0a] sticky top-0 h-screen">
          <div className="p-6 border-b border-[#2a2a2a]">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 flex flex-col overflow-hidden shadow-lg">
                <div className="flex-1 bg-[#00209F]" />
                <div className="flex-1 bg-[#D21034]" />
              </div>
              <div>
                <div className="font-display font-semibold tracking-tight text-lg leading-none">Rasin</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] mt-1">Haiti's Stage · 🇭🇹</div>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-200 rounded-sm border ${
                      isActive
                        ? "bg-[#1a1a1a] text-[#f5f5f5] border-[#ff4500]/40"
                        : "text-[#a3a3a3] hover:text-[#f5f5f5] hover:bg-[#141414] border-transparent"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div className="p-4 border-t border-[#2a2a2a]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-sm bg-[#1a1a1a] border border-[#2a2a2a] overflow-hidden flex items-center justify-center">
                {user?.avatar_url ? (
                  <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[#ff4500] font-display font-bold">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] truncate">{user?.email}</div>
              </div>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#ff4500] hover:bg-[#141414] border border-[#2a2a2a] rounded-sm transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </aside>

        {/* Mobile top nav */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-[#2a2a2a]">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex flex-col overflow-hidden">
                <div className="flex-1 bg-[#00209F]" />
                <div className="flex-1 bg-[#D21034]" />
              </div>
              <span className="font-display font-semibold tracking-tight">Rasin 🇭🇹</span>
            </div>
            <button onClick={onLogout} className="text-xs uppercase tracking-[0.2em] text-[#a3a3a3]" data-testid="mobile-logout-btn">
              Sign out
            </button>
          </div>
          <div className="flex overflow-x-auto border-t border-[#2a2a2a]">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex-1 min-w-[80px] flex flex-col items-center justify-center py-2 text-[10px] uppercase tracking-[0.15em] gap-1 ${
                      isActive ? "text-[#ff4500]" : "text-[#a3a3a3]"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 min-w-0 pt-28 md:pt-0 pb-28">{children}</main>
      </div>

      <AudioPlayer />
    </div>
  );
}
